import { motion } from "framer-motion";
import { HandCoins, ShieldCheck, ClipboardCheck, HeadphonesIcon, Award } from "lucide-react";

/**
 * Blocco "Perché sceglierci" con il messaggio centrale sulla success fee.
 * Usato in Home (Landing) e nel Calcolatore.
 *
 * Props:
 *   variant: "full" (default, con hero success-fee) | "compact" (banner ridotto)
 *   className: opzionale, per override marginature
 */
export default function PuntiForza({ variant = "full", className = "" }) {
  if (variant === "compact") {
    return (
      <div
        className={`relative overflow-hidden rounded-lg border border-brand/40 bg-gradient-to-r from-brand/[0.10] via-brand/[0.03] to-transparent p-4 md:p-5 ${className}`}
        data-testid="punti-forza-compact"
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center">
            <HandCoins className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-2 px-2 py-0.5 bg-brand text-white rounded-full text-[10px] font-display uppercase tracking-[0.2em]">
              Success fee
            </div>
            <p className="mt-2 text-sm text-ink leading-snug">
              <strong className="text-ink">Ci paghi solo se la pratica viene approvata.</strong>{" "}
              <span className="text-ink-muted">Lo Studio incassa il suo compenso esclusivamente all&apos;approvazione della pratica in prima fase GSE — nessun rischio anticipato per la tua azienda.</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section
      className={`bg-white border-y border-line ${className}`}
      data-testid="punti-forza-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs font-display tracking-[0.3em] uppercase text-brand">Perché scegliere lo Studio</div>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl text-ink">
            Ci paghi <span className="text-brand">solo se vinci</span>
          </h2>
          <p className="mt-4 text-ink-muted text-sm">
            Lavoriamo a success fee: incassiamo il nostro compenso solo quando la tua pratica viene
            approvata in prima fase GSE. Nessun costo anticipato, nessun rischio per la tua azienda.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <ForzaCard
            featured
            icon={<HandCoins className="w-6 h-6" />}
            title="Success fee"
            desc={
              <>
                Compenso corrisposto <strong>solo all&apos;approvazione della pratica in prima fase GSE</strong>.
                Se non passa, non paghi.
              </>
            }
          />
          <ForzaCard
            icon={<Award className="w-6 h-6" />}
            title="Perizia asseverata inclusa"
            desc="Il costo della perizia tecnica giurata è già ricompreso nel nostro compenso — nessuna sorpresa in fattura."
          />
          <ForzaCard
            icon={<ClipboardCheck className="w-6 h-6" />}
            title="Pratica end-to-end"
            desc="Dalla simulazione al portale GSE, dalla comunicazione preventiva all&apos;interconnessione: seguiamo ogni passaggio."
          />
          <ForzaCard
            icon={<HeadphonesIcon className="w-6 h-6" />}
            title="Team dedicato"
            desc="Un referente unico per tutta la durata della pratica. Rispondiamo entro 24 ore lavorative."
          />
        </motion.div>

        <div className="mt-10 flex justify-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand/[0.06] border border-brand/30 rounded-full text-xs text-ink">
            <ShieldCheck className="w-4 h-4 text-brand" />
            <span>Oltre <strong>1.500</strong> pratiche seguite · <strong>+8 MLN€</strong> di beneficio fiscale ottenuto per i nostri clienti</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function ForzaCard({ icon, title, desc, featured = false }) {
  return (
    <div
      className={`relative rounded-lg p-6 ${
        featured
          ? "bg-ink text-white border border-brand shadow-brand-glow"
          : "bg-white border border-line hover:border-brand/40 hover:shadow-card transition"
      }`}
    >
      {featured && (
        <div className="absolute -top-3 left-6 px-2.5 py-0.5 bg-brand text-white text-[10px] font-display uppercase tracking-[0.2em] rounded-full">
          Il nostro modello
        </div>
      )}
      <div
        className={`w-11 h-11 rounded-full flex items-center justify-center ${
          featured ? "bg-brand text-white" : "bg-brand/10 text-brand"
        }`}
      >
        {icon}
      </div>
      <h3 className={`mt-4 font-display text-lg ${featured ? "text-white" : "text-ink"}`}>{title}</h3>
      <p className={`mt-2 text-sm leading-relaxed ${featured ? "text-white/80" : "text-ink-muted"}`}>{desc}</p>
    </div>
  );
}
